import type { AxiosResponse } from 'axios';

import { useApiResponse } from '@/composables/useApiResponse';
import { HTTP_STATUS_CODE_UNAUTHORIZED, QUERY_E2E_RESET } from '@/constants';
import { RouteNames } from '@/router/routeNames';
import useAuthStore from '@/stores/useAuthStore';
import useLocationsStore from '@/stores/useLocationsStore';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

import { e2eApi } from './generatedApi';

export const useE2EResetData = () => {
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const { selected } = storeToRefs(useLocationsStore());
    const apiResponseCode = useApiResponse();
    const queryClient = useQueryClient();
    const router = useRouter();

    return useMutation({
        mutationKey: [QUERY_E2E_RESET],
        mutationFn: async (): Promise<void> => {
            try {
                if (!isAuthenticated.value) {
                    throw new Error('Not authenticated', { cause: HTTP_STATUS_CODE_UNAUTHORIZED });
                }

                const response: AxiosResponse<void> = await e2eApi.apiV1E2eResetDelete();

                if (apiResponseCode(response.status).ok) {
                    return void 0;
                } else {
                    throw new Error(`Could not reset data. ${response.statusText}`);
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        onSuccess: () => {
            queryClient.removeQueries();
            selected.value = [];
            router.push({ name: RouteNames.Index });
            console.info('E2E data reset successfully.');
        },
        onError: error => {
            console.error('Error resetting E2E data:', error);
        },
    });
};
